import { useCallback, useEffect, useState } from 'react';

import { cfetch, BadStatusError } from '@confluence/network';

export type UseFetchSuggestionCountResult = {
	count: number;
	isLoading: boolean;
	isSuppressed: boolean;
	refetch: () => void;
};

export const useFetchSuggestionCount = (spaceKey?: string): UseFetchSuggestionCountResult => {
	const [count, setCount] = useState(0);
	const [isLoading, setIsLoading] = useState(false);
	const [isSuppressed, setIsSuppressed] = useState(false);

	const fetchCount = useCallback(async () => {
		if (!spaceKey) {
			setCount(0);
			setIsLoading(false);
			return;
		}

		setIsLoading(true);

		try {
			const response = await cfetch(
				`/wiki/rest/internal/1.0/spaces/${spaceKey}/remote-objects/suggestions/count`,
				{ method: 'GET' },
			);

			const text = await response.text();
			setCount(parseInt(text, 10) || 0);
			setIsSuppressed(false);
		} catch (error) {
			if (BadStatusError.isBadStatusError(error) && error.response.status === 404) {
				setIsSuppressed(true);
			}
		} finally {
			setIsLoading(false);
		}
	}, [spaceKey]);

	useEffect(() => {
		// eslint-disable-next-line @atlassian/perf-linting/no-chain-state-updates
		void fetchCount();
	}, [fetchCount]);

	return { count, isLoading, isSuppressed, refetch: fetchCount };
};
